/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  Chip,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Rating,
  Breadcrumbs,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';

/**
 * Props for the ThemePreview component
 */
interface ThemePreviewProps {
  /**
   * Optional title for the preview
   */
  title?: string;
  /**
   * Whether to show in compact mode
   */
  compact?: boolean;
}

/**
 * Sample data for preview components
 */
const sampleData = {
  tableRows: [
    {
      name: 'Ballet Class',
      instructor: 'Sarah Johnson',
      students: 15,
      time: '9:00 AM',
    },
    {
      name: 'Hip-Hop Workshop',
      instructor: 'Mike Chen',
      students: 12,
      time: '2:00 PM',
    },
    {
      name: 'Contemporary',
      instructor: 'Emma Rodriguez',
      students: 8,
      time: '6:00 PM',
    },
  ],
  listItems: [
    { name: 'Sarah Johnson', role: 'Lead Instructor', avatar: 'SJ' },
    { name: 'Mike Chen', role: 'Hip-Hop Specialist', avatar: 'MC' },
    { name: 'Emma Rodriguez', role: 'Contemporary Teacher', avatar: 'ER' },
  ],
};

/**
 * Live theme preview component showing sample UI elements
 * Demonstrates how the current theme looks across different components
 */
const ThemePreview: React.FC<ThemePreviewProps> = ({
  title = 'Live Preview',
  compact = false,
}) => {
  const [tabValue, setTabValue] = React.useState(0);
  const [selectValue, setSelectValue] = React.useState('option1');
  const [checkboxValue, setCheckboxValue] = React.useState(true);
  const [ratingValue, setRatingValue] = React.useState(4);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        p: compact ? 2 : 3,
        backgroundColor: 'background.default',
        minHeight: '100%',
      }}
    >
      {/* Preview Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          See how your theme looks across different components in real-time
        </Typography>
      </Box>

      {/* Buttons Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Buttons
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="contained" color="primary">
            Primary Button
          </Button>
          <Button variant="contained" color="secondary">
            Secondary Button
          </Button>
          <Button variant="outlined" color="primary">
            Outlined
          </Button>
          <Button variant="text" color="primary">
            Text Button
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" size="small">
            Success
          </Button>
          <Button variant="contained" color="warning" size="small">
            Warning
          </Button>
          <Button variant="contained" color="error" size="small">
            Error
          </Button>
        </Box>
      </Box>

      {/* Cards Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Cards & Surfaces
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Class Schedule
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Manage your studio&apos;s daily classes and workshops
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label="15 Classes" size="small" color="primary" />
                <Chip label="8 Instructors" size="small" variant="outlined" />
              </Box>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Student Progress
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track performance and achievements
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Overall Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={75}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  75% Complete
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Forms Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Form Elements
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          <TextField
            label="Student Name"
            placeholder="Enter student name"
            variant="outlined"
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Class Type</InputLabel>
            <Select
              value={selectValue}
              label="Class Type"
              onChange={e => setSelectValue(e.target.value)}
            >
              <MenuItem value="option1">Ballet</MenuItem>
              <MenuItem value="option2">Hip-Hop</MenuItem>
              <MenuItem value="option3">Contemporary</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkboxValue}
                onChange={e => setCheckboxValue(e.target.checked)}
                color="primary"
              />
            }
            label="Send email notifications"
          />
        </Box>
      </Box>

      {/* Alerts Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Status Messages
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Alert severity="success">Class schedule updated successfully!</Alert>
          <Alert severity="warning">Registration deadline is approaching</Alert>
          <Alert severity="error">Payment method needs to be updated</Alert>
          <Alert severity="info">
            New features available in your dashboard
          </Alert>
        </Box>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Navigation
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs>
            <Typography
              component="button"
              sx={{
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Studio
            </Typography>
            <Typography
              component="button"
              sx={{
                border: 'none',
                background: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Classes
            </Typography>
            <Typography color="primary">Ballet Beginner</Typography>
          </Breadcrumbs>
        </Box>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<DashboardIcon />} label="Overview" />
          <Tab icon={<GroupsIcon />} label="Students" />
          <Tab icon={<SettingsIcon />} label="Settings" />
        </Tabs>
      </Box>

      {/* Data Display Section */}
      {!compact && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Data Display
          </Typography>

          {/* Table */}
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Class Name</TableCell>
                  <TableCell>Instructor</TableCell>
                  <TableCell align="right">Students</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleData.tableRows.map(row => (
                  <TableRow key={row.name}>
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell>{row.instructor}</TableCell>
                    <TableCell align="right">{row.students}</TableCell>
                    <TableCell>{row.time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* List */}
          <Paper>
            <List>
              {sampleData.listItems.map(item => (
                <React.Fragment key={item.name}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {item.avatar}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={item.name} secondary={item.role} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating
                        value={ratingValue}
                        onChange={(event, newValue) =>
                          setRatingValue(newValue || 0)
                        }
                        size="small"
                        readOnly
                      />
                      <Typography variant="caption" color="text.secondary">
                        4.8
                      </Typography>
                    </Box>
                  </ListItem>
                  {item.name !==
                    sampleData.listItems[sampleData.listItems.length - 1]
                      .name && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {/* Interactive Elements */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          Interactive Elements
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Rating
            </Typography>
            <Rating
              value={ratingValue}
              onChange={(event, newValue) => setRatingValue(newValue || 0)}
              size="small"
            />
          </Box>
          <Box>
            <Typography variant="caption" display="block" gutterBottom>
              Status Chips
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip label="Active" color="success" size="small" />
              <Chip label="Pending" color="warning" size="small" />
              <Chip label="Inactive" color="default" size="small" />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Preview Footer */}
      <Box
        sx={{
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          This preview updates in real-time as you adjust your theme colors
        </Typography>
      </Box>
    </Box>
  );
};

export default ThemePreview;
